import { useState, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, User, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import AnalyticsDisplay from './AnalyticsDisplay';

const MessageBubble = memo(function MessageBubble({ message, isStreaming }) {
  const [copied, setCopied] = useState(false);
  const [showToolCalls, setShowToolCalls] = useState(false);
  
  const isUser = message.role === 'user';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'message-enter group flex gap-4 py-6',
        isUser ? 'flex-row-reverse' : ''
      )}
      data-testid={`message-${message.message_id}`}
    >
      {/* Avatar */}
      <div
        className={cn(
          'shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser
            ? 'bg-primary/10 text-primary'
            : 'bg-gradient-to-br from-primary to-secondary text-white'
        )}
      >
        {isUser ? (
          <User className="w-4 h-4" />
        ) : (
          <Sparkles className="w-4 h-4" />
        )}
      </div>

      {/* Content */}
      <div className={cn('flex-1 max-w-3xl', isUser ? 'text-right' : '')}>
        {/* Tool calls trace */}
        {message.toolCalls?.length > 0 && (
          <div className="mb-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground gap-1"
              onClick={() => setShowToolCalls(!showToolCalls)}
              data-testid="toggle-tool-calls"
            >
              {showToolCalls ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {message.toolCalls.length} tool call{message.toolCalls.length > 1 ? 's' : ''}
            </Button>
            
            {showToolCalls && (
              <div className="mt-2 p-3 rounded-lg bg-muted/50 border border-border/50 text-left">
                {message.toolCalls.map((call, idx) => (
                  <div key={idx} className="text-xs font-mono">
                    <span className="text-primary">{call.tool}</span>
                    <span className="text-muted-foreground">
                      ({JSON.stringify(call.arguments)})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Message content */}
        <div
          className={cn(
            'inline-block text-left rounded-2xl px-4 py-3',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-card/50 backdrop-blur-sm border border-border/50'
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className={cn('markdown-content prose prose-sm dark:prose-invert max-w-none', isStreaming && 'streaming-cursor')}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    const codeContent = String(children).replace(/\n$/, '');
                    
                    if (!inline && match) {
                      return (
                        <div className="relative group/code">
                          <div className="absolute right-2 top-2 opacity-0 group-hover/code:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={async () => {
                                await navigator.clipboard.writeText(codeContent);
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <pre className={className} {...props}>
                            <code>{children}</code>
                          </pre>
                        </div>
                      );
                    }
                    return (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  table({ children }) {
                    return (
                      <div className="overflow-x-auto my-4">
                        <table className="min-w-full">{children}</table>
                      </div>
                    );
                  },
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Analytics display */}
        {message.analytics && (
          <div className="mt-4">
            <AnalyticsDisplay data={message.analytics} />
          </div>
        )}

        {/* Actions */}
        {!isUser && !isStreaming && message.content && (
          <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleCopy}
              data-testid="copy-message-btn"
            >
              {copied ? (
                <Check className="w-3 h-3 text-accent" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

export default MessageBubble;
